// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Upload, MoreHorizontal, Check, X, ChevronDown,
  Settings, LogOut, RefreshCw, Activity,
  AlertTriangle, FileText, Table2, Archive, File, ArrowRight,
} from 'lucide-react';
import Logo from '../components/shared/Logo.jsx';
import PlanBadge from '../components/shared/PlanBadge.jsx';
import PaywallModal from '../components/shared/PaywallModal.jsx';
import { api } from '../utils/api.js';
import { clearSession, getUser } from '../utils/auth.js';
import { PLAN_LIMITS as PLAN_LIMITS_CONFIG } from '../utils/planLimits.js';

const STATUS_COLORS = {
  uploaded:   'var(--color-primary)',
  parsing:    'var(--color-primary)',
  extracting: 'var(--color-primary)',
  review:     'var(--color-green)',
  exported:   'var(--color-green)',
  failed:     'var(--color-red)',
  completed_with_warnings: 'var(--color-green)',
};

const STATUS_BADGE = {
  uploaded:   { label: 'Pending',    bg: 'var(--color-surface-2)',      color: 'var(--color-text-3)' },
  parsing:    { label: 'Processing', bg: 'var(--color-primary-light)',   color: 'var(--cc-blue)' },
  extracting: { label: 'Processing', bg: 'var(--color-primary-light)',   color: 'var(--cc-blue)' },
  review:     { label: 'Review',     bg: 'var(--color-yellow-bg)',       color: 'var(--color-yellow-text)' },
  exported:   { label: 'Exported',   bg: 'var(--color-green-bg)',        color: 'var(--color-green-text)' },
  failed:     { label: 'Error',      bg: 'var(--color-red-bg)',          color: 'var(--color-red-text)' },
  completed_with_warnings: { label: 'Review', bg: 'var(--color-yellow-bg)', color: 'var(--color-yellow-text)' },
};

const FILE_TYPE_STYLES = {
  pdf:   { bg: 'var(--color-red-bg)',       color: 'var(--color-red-text)',   label: 'PDF' },
  excel: { bg: 'var(--color-green-bg)',     color: 'var(--color-green-text)', label: 'XLS' },
  csv:   { bg: 'var(--color-primary-light)', color: 'var(--cc-blue)',         label: 'CSV' },
  image: { bg: 'var(--color-primary-light)', color: 'var(--cc-blue)',          label: 'IMG' },
};

const PLAN_LIMITS = {
  starter: PLAN_LIMITS_CONFIG.starter.catalogs_per_month,
  growth:  PLAN_LIMITS_CONFIG.growth.catalogs_per_month,
  pro:     PLAN_LIMITS_CONFIG.pro.catalogs_per_month,
};

const SORT_LABELS = {
  newest:  'Newest first',
  oldest:  'Oldest first',
  name:    'Name (A–Z)',
};

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function getGreeting() {
  const h = new Date().getHours();
  if (h >= 0 && h < 5)  return 'Good night';
  if (h >= 5 && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getDisplayName(user) {
  const full = user?.user_metadata?.full_name || user?.user_metadata?.name;
  if (full) return full.split(' ')[0];
  const emailName = user?.email?.split('@')[0] || '';
  return emailName.replace(/[0-9]/g, '').replace(/\./g, ' ').trim().split(' ')[0] || 'there';
}

function stripExtension(name) {
  return name.replace(/\.(pdf|xlsx|xls|csv)$/i, '');
}

function getFileType(catalog) {
  const name = (catalog.file_name || catalog.original_filename || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'pdf';
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'excel';
  if (name.endsWith('.csv')) return 'csv';
  if (/\.(png|jpg|jpeg|webp|gif)$/.test(name)) return 'image';
  const t = (catalog.file_type || '').toLowerCase();
  if (t.includes('pdf')) return 'pdf';
  if (t.includes('excel') || t.includes('spreadsheet')) return 'excel';
  if (t.includes('csv')) return 'csv';
  if (t.includes('image')) return 'image';
  return 'pdf';
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return 'Uploaded ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function sortCatalogs(list, order) {
  const sorted = [...list];
  if (order === 'newest') return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  if (order === 'oldest') return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  if (order === 'name') return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return sorted;
}

function computeChecklistSteps(catalogs) {
  return [
    {
      id: 'upload',
      label: 'Upload your first catalog',
      done: catalogs.length > 0,
      cta: 'Upload now',
    },
    {
      id: 'review',
      label: 'Review extracted products',
      done: catalogs.some(c => c.status === 'review' || c.status === 'exported'),
      cta: 'Go to review table',
    },
    {
      id: 'export',
      label: 'Export your Shopify CSV',
      done: catalogs.some(c => c.status === 'exported'),
      cta: 'Export now',
    },
  ];
}

const chipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'var(--color-surface-2)',
  borderRadius: 6, padding: '3px 8px',
  fontSize: 12, color: 'var(--color-text-2)', fontWeight: 500,
};

const menuItemStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', textAlign: 'left',
  background: 'none', border: 'none', cursor: 'pointer',
  padding: '8px 12px', fontSize: 14, borderRadius: 6,
  color: 'var(--color-text)',
};

function StatCell({ value, label }) {
  return (
    <div style={{ background: 'var(--color-surface-2)', borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)' }}>{value ?? '—'}</div>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-3)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function CatalogCard({
  catalog, jobData, cardMenuOpen, deleteConfirm, retryConfirm,
  deletingId, retrying,
  onCardClick, onMenuToggle, onMenuClose,
  onOpenClick, onDeleteClick, onDeleteConfirm, onDeleteCancel,
  onRetryFromMenu, onRetryConfirm, onRetryCancel,
}) {
  const menuBtnRef = useRef(null);
  const fileType = getFileType(catalog);
  const fileStyle = FILE_TYPE_STYLES[fileType] || FILE_TYPE_STYLES.pdf;
  const badge = STATUS_BADGE[catalog.status] || STATUS_BADGE.uploaded;
  const statusColor = STATUS_COLORS[catalog.status] || 'var(--color-border-strong)';
  const isProcessing = catalog.status === 'parsing' || catalog.status === 'extracting';
  const progress = jobData?.progress_pct || 0;
  const displayName = stripExtension(catalog.name || catalog.file_name || 'Untitled catalog');
  const fileName = catalog.file_name || catalog.original_filename || '';

  if (deleteConfirm) {
    return (
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-red-border)',
          borderRadius: 14, padding: '18px 20px',
          opacity: deletingId ? 0 : 1,
          transition: 'opacity 0.3s',
        }}
      >
        <p style={{ fontSize: 14, color: 'var(--color-text)', fontWeight: 500, margin: '0 0 12px' }}>
          Delete <strong>{displayName}</strong> and all its products? This cannot be undone.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onDeleteConfirm}
            disabled={!!deletingId}
            style={{
              background: 'var(--cc-red)', color: 'var(--cc-white)', border: 'none',
              borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600,
              cursor: deletingId ? 'not-allowed' : 'pointer', opacity: deletingId ? 0.7 : 1,
            }}
          >
            {deletingId ? 'Deleting…' : 'Delete'}
          </button>
          <button
            onClick={onDeleteCancel}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-2)', padding: 0 }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (retryConfirm) {
    return (
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 14, padding: '18px 20px',
        }}
      >
        <p style={{ fontSize: 14, color: 'var(--color-text)', fontWeight: 500, margin: '0 0 12px' }}>
          Retry processing <strong>{displayName}</strong>?
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onRetryConfirm}
            disabled={!!retrying}
            style={{
              background: 'var(--color-primary)', color: 'var(--cc-white)', border: 'none',
              borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600,
              cursor: retrying ? 'not-allowed' : 'pointer', opacity: retrying ? 0.7 : 1,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <RefreshCw size={13} strokeWidth={2} />
            {retrying ? 'Retrying…' : 'Retry processing'}
          </button>
          <button
            onClick={onRetryCancel}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-2)', padding: 0 }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="cat-card"
      onClick={onCardClick}
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 14, padding: '16px 20px',
        cursor: 'pointer', display: 'flex',
        alignItems: 'center', gap: 16,
        position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: 3, borderRadius: '14px 0 0 14px',
        background: statusColor,
      }} />

      <div style={{
        width: 40, height: 40, borderRadius: 8, flexShrink: 0,
        background: fileStyle.bg, color: fileStyle.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, letterSpacing: '0.03em',
      }}>
        {fileStyle.label}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </div>
        {fileName && (
          <div style={{ fontSize: 12, color: 'var(--color-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fileName}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--color-text-3)' }}>
          {formatDate(catalog.created_at)}
        </div>
        {(catalog.status === 'review' || catalog.status === 'exported') && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {catalog.page_count != null && (
              <span style={chipStyle}>{catalog.page_count} pages</span>
            )}
            <span style={chipStyle}>{catalog.product_count || 0} products</span>
            {catalog.error_count > 0 ? (
              <span style={{ ...chipStyle, background: 'var(--color-red-bg)', color: 'var(--color-red-text)' }}>
                {catalog.error_count} errors
              </span>
            ) : (
              <span style={{ ...chipStyle, background: 'var(--color-green-bg)', color: 'var(--color-green-text)' }}>
                Ready
              </span>
            )}
          </div>
        )}
        {catalog.status === 'review' && (
          <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 4 }}>Review before exporting</div>
        )}
        {catalog.status === 'exported' && (
          <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 4 }}>Ready for Shopify</div>
        )}
        {(catalog.status === 'parsing' || catalog.status === 'extracting') && (
          <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 4 }}>Processing your catalog...</div>
        )}
        {isProcessing && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--color-border)' }}>
            <div style={{ height: 4, borderRadius: 999, background: 'var(--color-border)', overflow: 'hidden' }}>
              <div style={{
                height: '100%', background: 'var(--color-primary)', borderRadius: 999,
                width: `${progress}%`, transition: 'width 0.5s ease',
              }} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-text-3)', marginTop: 4 }}>
              {catalog.page_count > 0
                ? `${catalog.pages_scanned || 0} of ${catalog.page_count} pages scanned`
                : progress > 0 ? `${Math.round(progress)}% complete` : 'Starting…'}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span
          title={catalog.status === 'review' ? 'Your products are ready to check and edit — click to open the review table.' : undefined}
          style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '4px 10px', borderRadius: 999,
            fontSize: 12, fontWeight: 600,
            background: badge.bg, color: badge.color,
            whiteSpace: 'nowrap',
            cursor: catalog.status === 'review' ? 'help' : undefined,
          }}
        >
          {badge.label}
        </span>

        {catalog.status === 'failed' ? (
          <button
            onClick={e => { e.stopPropagation(); onRetryFromMenu(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: '1px solid var(--color-border)',
              borderRadius: 8, padding: '5px 10px',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              color: 'var(--color-primary)',
            }}
          >
            <RefreshCw size={13} strokeWidth={2} /> Retry
          </button>
        ) : (
          <button
            ref={menuBtnRef}
            className="cat-dots"
            onClick={e => onMenuToggle(e, menuBtnRef.current?.getBoundingClientRect())}
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: 'none',
              border: '1px solid var(--color-border)',
              cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: 'var(--color-text-2)',
            }}
          >
            <MoreHorizontal size={15} strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Action needed section (Patch brief 2026-05-11 DPP.2/3) ───────────────
// Surfaces drops the merchant lost track of: needs_user_decision (picker not
// resolved), no_catalog_detected (Layer 1 rejection), or failed catalogs
// within the last 30 days. Hidden entirely when the list is empty so the
// happy-path dashboard stays clean.

const PENDING_STATUS_COPY: Record<string, { subtitle: string; cta: string; borderColor: string; iconColor: string }> = {
  needs_user_decision: {
    subtitle: 'Multiple catalogs detected · Pick the primary',
    cta: 'Pick primary →',
    borderColor: 'var(--color-yellow-border)',
    iconColor: 'var(--color-yellow)',
  },
  no_catalog_detected: {
    subtitle: 'No catalog content found',
    cta: 'Review →',
    borderColor: 'var(--color-red-border)',
    iconColor: 'var(--color-red)',
  },
  failed: {
    subtitle: '', // populated from failure_reason at row-render time
    cta: 'View details →',
    borderColor: 'var(--color-red-border)',
    iconColor: 'var(--color-red)',
  },
};

function fileTypeIcon(fileType: string | null | undefined) {
  const t = (fileType || '').toLowerCase();
  if (t === 'pdf') return FileText;
  if (t === 'xlsx' || t === 'csv') return Table2;
  if (t === 'zip') return Archive;
  return File;
}

function ActionNeededSection({ drops, onOpen }: { drops: any[]; onOpen: (dropId: string) => void }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 12px' }}>
        Action needed
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {drops.map((d) => (
          <PendingDropRow key={d.drop_id} drop={d} onClick={() => onOpen(d.drop_id)} />
        ))}
      </div>
    </div>
  );
}

function PendingDropRow({ drop, onClick }: { drop: any; onClick: () => void }) {
  const copy = PENDING_STATUS_COPY[drop.status] || PENDING_STATUS_COPY.failed;
  const Icon = fileTypeIcon(drop.file_type);
  const title = drop.status === 'failed' ? (drop.filename || 'Failed extraction') : (drop.filename || 'Drop');
  const subtitle = drop.status === 'failed' ? (drop.failure_reason || 'Extraction failed') : copy.subtitle;

  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px', borderRadius: 10,
        background: 'var(--color-surface)',
        border: `1.5px solid ${copy.borderColor}`,
        cursor: 'pointer', textAlign: 'left', width: '100%',
      }}
    >
      <AlertTriangle size={18} style={{ color: copy.iconColor, flexShrink: 0 }} />
      <Icon size={18} style={{ color: 'var(--color-text-2)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-2)', marginTop: 2 }}>
          {subtitle}
        </div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-primary)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
        {copy.cta}
      </span>
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  const [catalogs, setCatalogs] = useState([]);
  const [pendingDrops, setPendingDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checklistDismissed, setChecklistDismissed] = useState(
    () => { try { return localStorage.getItem('cc_checklist_dismissed') === 'true'; } catch { return false; } }
  );
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [jobData, setJobData] = useState({});
  const [cardMenuOpen, setCardMenuOpen] = useState(null);
  const [menuRect, setMenuRect] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [retryConfirm, setRetryConfirm] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [userState, setUserState] = useState(() => getUser() || {});

  const pollingRef = useRef({});
  const jobIdRef = useRef({});

  const user = userState;
  const plan = user.plan || 'free';
  const previewUsed = user.preview_used || false;
  const catalogsThisMonth = user.catalogs_processed_this_month || 0;
  const billingCycleDay = user.billing_cycle_day ?? null;
  const resetLine = plan === 'free'
    ? null
    : billingCycleDay
      ? `Resets on the ${ordinal(billingCycleDay)}`
      : 'Resets monthly';
  const planLimit = PLAN_LIMITS[plan] || 0;
  const usagePct = plan !== 'free' && planLimit > 0 ? Math.min((catalogsThisMonth / planLimit) * 100, 100) : 0;
  const usageBarColor = usagePct >= 100 ? 'var(--cc-red)' : usagePct >= 80 ? 'var(--cc-amber)' : 'var(--cc-green)';
  const showUpgradeNudge = (plan === 'free' && previewUsed) || (plan !== 'free' && usagePct >= 80);
  const showUpgradeLink = plan === 'free' || plan === 'starter' || plan === 'growth';
  const planNames = { free: 'Free', starter: 'Starter', growth: 'Growth', pro: 'Pro' };
  const planName = planNames[plan] || plan.charAt(0).toUpperCase() + plan.slice(1);
  const remaining = plan !== 'free' && planLimit > 0 ? Math.max(0, planLimit - catalogsThisMonth) : null;
  const isLow = remaining !== null && remaining <= 2;
  const userDisplayName = getDisplayName(user);
  const initial = userDisplayName !== 'there' ? userDisplayName.charAt(0).toUpperCase() : (user.email ? user.email.charAt(0).toUpperCase() : '?');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'success') {
      setCheckoutSuccess(true);
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [location.search]);

  const loadData = useCallback(async () => {
    try {
      const [catalogsData, meData] = await Promise.all([
        api.getCatalogs(),
        api.getMe().catch(() => null),
      ]);
      setCatalogs(catalogsData.catalogs || []);
      setPendingDrops(catalogsData.pendingDrops || []);
      if (meData) {
        const fresh = { ...JSON.parse(localStorage.getItem('cc_user') || '{}'), ...meData };
        try { localStorage.setItem('cc_user', JSON.stringify(fresh)); } catch { /* quota */ }
        setUserState(fresh);
      }
    } catch (e) {
      console.error('Dashboard load error:', e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    Object.keys(pollingRef.current).forEach(id => {
      const catalog = catalogs.find(c => c.id === id);
      if (!catalog || (catalog.status !== 'parsing' && catalog.status !== 'extracting')) {
        clearInterval(pollingRef.current[id]);
        delete pollingRef.current[id];
        delete jobIdRef.current[id];
      }
    });

    catalogs
      .filter(c => (c.status === 'parsing' || c.status === 'extracting') && !pollingRef.current[c.id])
      .forEach(catalog => {
        const cid = catalog.id;
        pollingRef.current[cid] = setInterval(async () => {
          try {
            if (!jobIdRef.current[cid]) {
              const { job } = await api.getCatalogJob(cid);
              if (!job) return;
              jobIdRef.current[cid] = job.id;
            }
            const jobId = jobIdRef.current[cid];
            const [status, catData] = await Promise.all([
              api.getJobStatus(jobId),
              api.getCatalog(cid).catch(() => null),
            ]);
            if (!status) return;
            setJobData(prev => ({ ...prev, [cid]: status }));
            if (catData?.catalog) {
              setCatalogs(prev => prev.map(c =>
                c.id === cid ? { ...c, pages_scanned: catData.catalog.pages_scanned, page_count: catData.catalog.page_count } : c
              ));
            }
            if (status.status === 'complete' || status.status === 'completed_with_warnings' || status.status === 'failed') {
              clearInterval(pollingRef.current[cid]);
              delete pollingRef.current[cid];
              delete jobIdRef.current[cid];
              const data = await api.getCatalogs();
              setCatalogs(data.catalogs || []);
              setPendingDrops(data.pendingDrops || []);
            }
          } catch (e) { console.warn('[Dashboard] poll error:', e?.message); }
        }, 5000);
      });
  }, [catalogs]);

  useEffect(() => {
    return () => { Object.values(pollingRef.current).forEach(clearInterval); };
  }, []);

  useEffect(() => {
    if (!cardMenuOpen && !avatarMenuOpen && !sortOpen) return;
    const close = () => {
      setCardMenuOpen(null);
      setMenuRect(null);
      setAvatarMenuOpen(false);
      setSortOpen(false);
    };
    document.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [cardMenuOpen, avatarMenuOpen, sortOpen]);

  const handleSignOut = async () => {
    try { await api.logout(); } catch {}
    clearSession();
    navigate('/login');
  };

  const handleUploadClick = () => {
    if (plan === 'free' && previewUsed) {
      setShowUpgradeModal(true);
    } else {
      navigate('/upload');
    }
  };

  const handleCardClick = async (catalog) => {
    if (deleteConfirm === catalog.id || retryConfirm === catalog.id) return;
    if (catalog.status === 'review' || catalog.status === 'exported') {
      navigate(`/review/${catalog.id}`);
    } else if (catalog.status === 'failed') {
      setRetryConfirm(catalog.id);
    } else {
      // In-progress: fetch job_id so ReviewTable shows ProcessingFeed banner
      try {
        const { job } = await api.getCatalogJob(catalog.id);
        navigate(`/review/${catalog.id}${job?.id ? `?job_id=${job.id}` : ''}`);
      } catch {
        navigate(`/review/${catalog.id}`);
      }
    }
  };

  const handleDeleteConfirm = async (catalogId) => {
    setDeletingId(catalogId);
    try {
      await api.deleteCatalog(catalogId);
      setTimeout(() => {
        setCatalogs(prev => prev.filter(c => c.id !== catalogId));
        setDeletingId(null);
        setDeleteConfirm(null);
      }, 300);
    } catch (e) {
      console.error('[Dashboard] delete error:', e?.message);
      setDeletingId(null);
    }
  };

  const handleRetry = async (catalogId) => {
    setRetrying(catalogId);
    try {
      await api.retryJob(catalogId);
      setRetryConfirm(null);
      setCatalogs(prev => prev.map(c =>
        c.id === catalogId ? { ...c, status: 'parsing' } : c
      ));
    } catch (e) {
      console.error('[Dashboard] retry error:', e?.message);
    }
    setRetrying(null);
  };

  const mostRecent = (arr) => arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  const handleChecklistCta = (step) => {
    if (step.id === 'upload') {
      handleUploadClick();
    } else if (step.id === 'review') {
      const target = mostRecent(catalogs.filter(c => c.status === 'review' || c.status === 'exported'));
      if (target) navigate(`/review/${target.id}`);
    } else if (step.id === 'export') {
      const target = mostRecent(catalogs.filter(c => c.status === 'exported'));
      if (target) navigate(`/review/${target.id}`);
    }
  };

  const dismissChecklist = () => {
    try { localStorage.setItem('cc_checklist_dismissed', 'true'); } catch { /* quota */ }
    setChecklistDismissed(true);
  };

  const sortedCatalogs = sortCatalogs(catalogs, sortOrder).filter(c =>
    !searchQuery || (c.name || c.file_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );
  const menuOpenCatalog = cardMenuOpen ? catalogs.find(c => c.id === cardMenuOpen) : null;
  const checklistSteps = computeChecklistSteps(catalogs);
  const allChecklistDone = checklistSteps.every(s => s.done);
  const showChecklist = catalogs.length > 0 && !checklistDismissed && !allChecklistDone;
  const needsAttentionCount = catalogs.filter(c => c.status === 'review' || c.status === 'failed').length;
  const allDoneExported = catalogs.length > 0 && catalogs.every(c => c.status === 'exported');
  const subtitleLine = catalogs.length === 0
    ? null
    : needsAttentionCount > 0
      ? `You have ${needsAttentionCount} catalog${needsAttentionCount > 1 ? 's' : ''} that need${needsAttentionCount === 1 ? 's' : ''} attention.`
      : allDoneExported
        ? 'Everything is on track.'
        : null;
  const completedSteps = checklistSteps.filter(s => s.done).length;
  const firstIncomplete = checklistSteps.find(s => !s.done);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', fontFamily: 'var(--font-sans, Inter, sans-serif)' }}>
      <style>{`
        :root { --cc-amber: var(--color-yellow); --cc-green: var(--color-green); --cc-red: var(--color-red); --cc-blue: var(--color-primary); --cc-white: white; }
        .cat-dots { opacity: 0.4; transition: opacity 0.15s; }
        .cat-dots:hover { opacity: 1; }
        .cat-card { transition: border-color 0.15s, box-shadow 0.15s; }
        .cat-card:hover { border-color: var(--color-border-strong) !important; box-shadow: 0 2px 8px rgba(0,0,0,0.07) !important; }
        .menu-item:hover { background: var(--color-surface-2) !important; }
        .upload-btn:hover { filter: brightness(0.92); }
        @keyframes shimmer { 0% { background-position: -800px 0; } 100% { background-position: 800px 0; } }
        .skel { background: linear-gradient(90deg, var(--color-surface-2) 25%, var(--color-border) 50%, var(--color-surface-2) 75%); background-size: 800px 100%; animation: shimmer 1.4s infinite; border-radius: 14px; height: 80px; margin-bottom: 12px; }
        @media (max-width: 900px) { .dash-layout { flex-direction: column !important; } .dash-sidebar { width: 100% !important; min-width: 0 !important; } }
      `}</style>

      {menuOpenCatalog && menuRect && !deleteConfirm && !retryConfirm && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: menuRect.bottom + 6,
            right: window.innerWidth - menuRect.right,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            padding: 6, minWidth: 160, zIndex: 9999,
          }}
        >
          <button
            className="menu-item"
            onClick={() => {
              setCardMenuOpen(null);
              setMenuRect(null);
              if (menuOpenCatalog.status === 'review' || menuOpenCatalog.status === 'exported')
                navigate(`/review/${menuOpenCatalog.id}`);
              else navigate(`/processing/${menuOpenCatalog.id}`);
            }}
            style={menuItemStyle}
          >Open</button>
          {menuOpenCatalog.status === 'failed' && (
            <button
              className="menu-item"
              onClick={() => { setCardMenuOpen(null); setMenuRect(null); handleRetry(menuOpenCatalog.id); }}
              style={{ ...menuItemStyle, color: 'var(--color-primary)' }}
            >
              <RefreshCw size={13} strokeWidth={1.5} /> Retry processing
            </button>
          )}
          <button
            className="menu-item"
            onClick={() => { setCardMenuOpen(null); setMenuRect(null); setDeleteConfirm(menuOpenCatalog.id); }}
            style={{ ...menuItemStyle, color: 'var(--cc-red)' }}
          >Delete</button>
        </div>
      )}


      <header style={{
        position: 'sticky', top: 0, zIndex: 20, height: 60,
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        display: 'flex', alignItems: 'center',
        padding: '0 24px', gap: 12,
      }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 0 }}>
          <Logo size={32} />
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            onClick={() => plan === 'free' ? setShowUpgradeModal(true) : navigate('/settings?tab=billing')}
            style={{
              cursor: 'pointer',
              background: isLow ? 'var(--color-yellow-bg)' : 'var(--color-surface-2)',
              border: isLow ? '1px solid var(--color-yellow-border)' : '1px solid var(--color-border)',
              borderRadius: 6,
              fontSize: 12, fontWeight: 500,
              color: isLow ? 'var(--color-yellow-text)' : 'var(--color-text-2)',
              padding: '4px 10px',
              userSelect: 'none',
            }}
          >
            {planName} plan{isLow ? ` · ${remaining} left` : ''}
          </span>

          <button
            className="upload-btn"
            onClick={handleUploadClick}
            style={{
              background: 'var(--color-primary)', color: 'var(--cc-white)',
              border: 'none', borderRadius: 10,
              padding: '8px 16px', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <Upload size={14} strokeWidth={2} />
            Upload catalog
          </button>

          <div style={{ position: 'relative' }}>
            <button
              onClick={e => { e.stopPropagation(); setAvatarMenuOpen(p => !p); }}
              style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--color-primary)',
                color: 'var(--cc-white)',
                border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {initial}
            </button>
            {avatarMenuOpen && (
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  padding: 6, minWidth: 160, zIndex: 100,
                }}
              >
                <button className="menu-item" onClick={() => { setAvatarMenuOpen(false); navigate('/settings'); }} style={menuItemStyle}>
                  <Settings size={14} strokeWidth={1.5} />
                  Settings
                </button>
                {user.is_admin && (
                  <button className="menu-item" onClick={() => { setAvatarMenuOpen(false); navigate('/admin'); }} style={menuItemStyle}>
                    <Activity size={14} strokeWidth={1.5} />
                    Admin
                  </button>
                )}
                <button className="menu-item" onClick={handleSignOut} style={menuItemStyle}>
                  <LogOut size={14} strokeWidth={1.5} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
        {loadError && (
          <div style={{
            background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)',
            borderRadius: 12, padding: '14px 20px', marginBottom: 24,
            display: 'flex', alignItems: 'center', gap: 10,
            color: 'var(--color-red-text)', fontSize: 14, fontWeight: 500,
          }}>
            <X size={16} strokeWidth={2} />
            Could not load your catalogs. Please refresh the page.
            <button
              onClick={() => { setLoadError(false); loadData(); }}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', textDecoration: 'underline', fontSize: 13 }}
            >
              Retry
            </button>
          </div>
        )}

        {checkoutSuccess && (
          <div style={{
            background: 'var(--color-green-bg)', border: '1px solid var(--color-green-border)',
            borderRadius: 12, padding: '14px 20px', marginBottom: 24,
            display: 'flex', alignItems: 'center', gap: 10,
            color: 'var(--color-green-text)', fontSize: 14, fontWeight: 500,
          }}>
            <Check size={16} strokeWidth={2} />
            You're upgraded! Your new plan is active. Happy converting.
            <button
              onClick={() => setCheckoutSuccess(false)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.6, padding: 0, display: 'flex' }}
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 6px' }}>
            {getGreeting()}, {userDisplayName}.
          </h1>
          {subtitleLine && (
            <p style={{ fontSize: 14, color: 'var(--color-text-2)', margin: 0 }}>
              {subtitleLine}
            </p>
          )}
        </div>

        <div className="dash-layout" style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {showChecklist && (
              <div style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 14, padding: '20px 24px', marginBottom: 20,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>
                    Get started with CatalogConvert
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 13, color: 'var(--color-text-2)' }}>
                      {completedSteps}/3 steps completed
                    </span>
                    <button
                      onClick={dismissChecklist}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)', padding: 0, display: 'flex', alignItems: 'center' }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
                <div style={{ height: 4, borderRadius: 999, background: 'var(--color-border)', margin: '12px 0 16px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 999,
                    background: 'var(--color-primary)',
                    width: `${(completedSteps / 3) * 100}%`,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
                {checklistSteps.map((step, i) => (
                  <div
                    key={step.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 0',
                      borderTop: i === 0 ? 'none' : '1px solid var(--color-border)',
                    }}
                  >
                    <div style={{
                      width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: step.done ? 'var(--cc-green)' : 'transparent',
                      border: step.done ? 'none' : '2px solid var(--color-border)',
                    }}>
                      {step.done && <Check size={11} color="var(--cc-white)" strokeWidth={3} />}
                    </div>
                    <span style={{
                      flex: 1, fontSize: 14,
                      fontWeight: step.done ? 400 : 500,
                      color: step.done ? 'var(--color-text-3)' : 'var(--color-text)',
                      textDecoration: step.done ? 'line-through' : 'none',
                    }}>
                      {step.label}
                    </span>
                    {!step.done && step.id === firstIncomplete?.id && (
                      <button
                        onClick={() => handleChecklistCta(step)}
                        style={{
                          background: 'var(--color-primary-light)', color: 'var(--color-primary)',
                          border: 'none', borderRadius: 8,
                          padding: '6px 12px', fontSize: 13, fontWeight: 600,
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        {step.cta}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Action needed — pending drops + failed catalogs (Patch brief 2026-05-11 DPP.2). */}
            {!loading && pendingDrops.length > 0 && (
              <ActionNeededSection
                drops={pendingDrops}
                onOpen={(dropId) => navigate(`/drops/${dropId}`)}
              />
            )}

            {loading ? (
              <>
                <div className="skel" />
                <div className="skel" />
                <div className="skel" />
              </>
            ) : catalogs.length === 0 ? (
              <div style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 14, padding: '56px 32px', textAlign: 'center',
              }}>
                <div style={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: 'var(--color-primary-light)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                }}>
                  <Upload size={28} color="var(--color-primary)" strokeWidth={1.5} />
                </div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text)', margin: '0 auto 8px', maxWidth: 400 }}>
                  Turn your first supplier catalog into Shopify products.
                </h2>
                <p style={{ fontSize: 14, color: 'var(--color-text-2)', lineHeight: 1.6, maxWidth: 380, margin: '0 auto 28px' }}>
                  Upload a PDF, Excel, CSV, or image. AI extracts every product in minutes. Review and export a Shopify-ready CSV.
                </p>
                <button
                  onClick={handleUploadClick}
                  style={{
                    background: 'var(--color-primary)', color: 'var(--cc-white)',
                    border: 'none', borderRadius: 10,
                    padding: '13px 28px', fontSize: 15, fontWeight: 700,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                >
                  Upload your first catalog →
                </button>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 16 }}>
                  {['PDF', 'Excel', 'CSV', 'Image'].map(f => (
                    <span key={f} style={{
                      background: 'var(--color-surface-2)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 999, padding: '4px 12px',
                      fontSize: 12, color: 'var(--color-text-3)', fontWeight: 500,
                    }}>{f}</span>
                  ))}
                </div>
                {plan === 'free' && (
                  <p style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 16, margin: '16px 0 0' }}>
                    No credit card required · One free catalog included
                  </p>
                )}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)', flexShrink: 0 }}>Your catalogs</span>
                  <div style={{ flex: 1, maxWidth: 280, minWidth: 120, position: 'relative' }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search catalogs..."
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8, padding: '6px 30px 6px 10px',
                        fontSize: 13, color: 'var(--color-text)',
                        background: 'var(--color-surface)',
                        outline: 'none',
                      }}
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        style={{
                          position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--color-text-3)', fontSize: 14, lineHeight: 1, padding: 2,
                        }}
                      >×</button>
                    )}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={e => { e.stopPropagation(); setSortOpen(p => !p); }}
                      style={{
                        background: 'none', border: '1px solid var(--color-border)',
                        borderRadius: 8, padding: '6px 12px',
                        fontSize: 13, color: 'var(--color-text-2)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      {SORT_LABELS[sortOrder]} <ChevronDown size={13} strokeWidth={2} />
                    </button>
                    {sortOpen && (
                      <div
                        onClick={e => e.stopPropagation()}
                        style={{
                          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                          background: 'var(--color-surface)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 10,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          padding: 6, minWidth: 160, zIndex: 50,
                        }}
                      >
                        {Object.entries(SORT_LABELS).map(([val, label]) => (
                          <button
                            key={val}
                            className="menu-item"
                            onClick={() => { setSortOrder(val); setSortOpen(false); }}
                            style={{
                              ...menuItemStyle,
                              fontWeight: sortOrder === val ? 700 : 400,
                              color: sortOrder === val ? 'var(--color-primary)' : 'var(--color-text)',
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {sortedCatalogs.map(catalog => (
                    <CatalogCard
                      key={catalog.id}
                      catalog={catalog}
                      jobData={jobData[catalog.id]}
                      cardMenuOpen={cardMenuOpen === catalog.id}
                      deleteConfirm={deleteConfirm === catalog.id}
                      retryConfirm={retryConfirm === catalog.id}
                      deletingId={deletingId === catalog.id}
                      retrying={retrying === catalog.id}
                      onCardClick={() => handleCardClick(catalog)}
                      onMenuToggle={(e, rect) => { e.stopPropagation(); const next = cardMenuOpen === catalog.id ? null : catalog.id; setCardMenuOpen(next); setMenuRect(next ? rect : null); setDeleteConfirm(null); setRetryConfirm(null); }}
                      onMenuClose={() => setCardMenuOpen(null)}
                      onOpenClick={() => {
                        setCardMenuOpen(null);
                        if (catalog.status === 'review' || catalog.status === 'exported') navigate(`/review/${catalog.id}`);
                        else navigate(`/processing/${catalog.id}`);
                      }}
                      onDeleteClick={() => { setCardMenuOpen(null); setDeleteConfirm(catalog.id); }}
                      onDeleteConfirm={() => handleDeleteConfirm(catalog.id)}
                      onDeleteCancel={() => setDeleteConfirm(null)}
                      onRetryFromMenu={() => { setCardMenuOpen(null); handleRetry(catalog.id); }}
                      onRetryConfirm={() => handleRetry(catalog.id)}
                      onRetryCancel={() => setRetryConfirm(null)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="dash-sidebar" style={{ width: 280, flexShrink: 0, minWidth: 240 }}>
            <div style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 14, padding: 20, marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>
                  {planName} plan
                </span>
                {showUpgradeLink && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--color-primary)', fontWeight: 500, padding: 0 }}
                  >
                    Upgrade →
                  </button>
                )}
              </div>

              <div style={{ marginTop: 16 }}>
                {plan === 'free' ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <p style={{ fontSize: 13, color: 'var(--color-text-2)', margin: 0 }}>1 free catalog</p>
                    </div>
                    <div style={{ height: 6, borderRadius: 999, background: 'var(--color-border)', overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{
                        height: '100%', borderRadius: 999,
                        background: 'var(--cc-green)',
                        width: previewUsed ? '100%' : '0%',
                        transition: 'width 0.4s ease',
                      }} />
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--color-text-3)', margin: 0 }}>
                      {previewUsed ? 'Used · Upgrade to process more' : '1 catalog upload included'}
                    </p>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <p style={{ fontSize: 13, color: 'var(--color-text-2)', margin: 0, fontVariantNumeric: 'tabular-nums' }}>
                        {catalogsThisMonth} of {planLimit} used
                      </p>
                      {isLow && (
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-yellow-text)' }}>
                          {remaining} left
                        </span>
                      )}
                    </div>
                    <div style={{ height: 6, borderRadius: 999, background: 'var(--color-border)', overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{
                        height: '100%', borderRadius: 999,
                        background: usageBarColor,
                        width: `${usagePct}%`,
                        transition: 'width 0.4s ease',
                      }} />
                    </div>
                    {resetLine && (
                      <p style={{ fontSize: 11, color: 'var(--color-text-3)', margin: 0 }}>
                        {resetLine}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {catalogs.length > 0 && (
              <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 20, marginTop: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Your activity
                </span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
                  <StatCell value={catalogs.length} label="Total catalogs" />
                  <StatCell value={catalogs.reduce((a, c) => a + (c.product_count || 0), 0)} label="Products extracted" />
                  <StatCell value={catalogs.filter(c => c.status === 'exported').length} label="Exported" />
                  <StatCell value={catalogs.filter(c => c.status === 'review').length} label="In review" />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {showUpgradeModal && (
        <PaywallModal onClose={() => setShowUpgradeModal(false)} trigger="dashboard" />
      )}
    </div>
  );
}
